import React, { useState, useMemo, useCallback, lazy, Suspense } from 'react';
import { blogPosts } from '../data/blogData';
import BlogCard from '../components/BlogCard';
import BlogSidebar from '../components/BlogSidebar';
import SkeletonCard from '../components/SkeletonCard';
import { FaSearch, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Fuse from 'fuse.js';

const BlogModal = lazy(() => import('../components/BlogModal'));

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('newest');
  const [viewAll, setViewAll] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fuse = useMemo(() => {
    return new Fuse(blogPosts, {
      keys: ['title', 'excerpt', 'tags', 'category'],
      threshold: 0.3,
      includeScore: true,
    });
  }, []);

  const filteredPosts = useMemo(() => {
    let posts = blogPosts;
    if (selectedCategory !== 'All') {
      posts = posts.filter(post => post.category === selectedCategory);
    }
    if (searchTerm.trim()) {
      const results = fuse.search(searchTerm);
      posts = results.map(result => result.item);
    }
    posts = [...posts].sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.date) - new Date(a.date);
      if (sortBy === 'oldest') return new Date(a.date) - new Date(b.date);
      if (sortBy === 'popular') return (b.likes || 0) - (a.likes || 0);
      return 0;
    });
    return posts;
  }, [selectedCategory, searchTerm, sortBy, fuse]);

  const featuredPost = filteredPosts[0];
  const recentPosts = filteredPosts.slice(1);

  const handleOpenPost = useCallback((post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPost(null);
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(blogPosts.map(post => post.category));
    return ['All', ...Array.from(cats)];
  }, []);

  return (
    <div className="min-h-screen pt-20 sm:pt-24 pb-16 bg-white dark:bg-gray-900">
      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-4 sm:mb-6">
            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
              Blog
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">
            Blog & <span className="gradient-text">Insights</span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed max-w-2xl mb-6">
            Thoughts on cybersecurity, development, and my journey in tech
          </p>

          <div className="flex gap-4">
            <Link
              to="#articles"
              className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors text-sm sm:text-base"
            >
              Browse Articles
              <FaArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 sm:py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm sm:text-base"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm sm:text-base"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm sm:text-base"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
        </div>
      </div>

      {/* Featured Article */}
      {featuredPost && !viewAll && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12">
          <div className="mb-4 sm:mb-6">
            <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
              Featured Article
            </span>
          </div>
          <Link to={`/blog/${featuredPost.slug}`} className="group block">
            <article className="relative rounded-2xl overflow-hidden bg-gray-900">
              <div className="aspect-[21/9] relative">
                {featuredPost.image ? (
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    loading="eager"
                    fetchpriority="high"
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-50 transition-opacity"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 md:p-12">
                <div className="max-w-3xl">
                  <span className="inline-block px-3 py-1 bg-emerald-600 text-white text-sm font-medium rounded-md mb-3 sm:mb-4">
                    {featuredPost.category}
                  </span>
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3 group-hover:text-emerald-400 transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-gray-300 text-sm sm:text-base lg:text-lg mb-3 sm:mb-4 line-clamp-2">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-gray-400">
                    <span>{featuredPost.author}</span>
                    <span>•</span>
                    <span>{featuredPost.date}</span>
                    <span>•</span>
                    <span>{featuredPost.readTime} min read</span>
                  </div>
                </div>
              </div>
            </article>
          </Link>
        </div>
      )}

      {/* Articles Grid */}
      <div id="articles" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8">
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                {viewAll ? 'All Articles' : 'Recent Articles'}
              </h2>
              {!viewAll && filteredPosts.length > 4 && (
                <button
                  onClick={() => setViewAll(true)}
                  className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
                >
                  View All →
                </button>
              )}
            </div>

            <Suspense fallback={
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            }>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {(viewAll ? filteredPosts : recentPosts.slice(0, 6)).map(post => (
                  <BlogCard
                    key={post.id}
                    post={post}
                    onOpen={handleOpenPost}
                  />
                ))}
              </div>
            </Suspense>

            {viewAll && (
              <div className="text-center mt-6 sm:mt-8">
                <button
                  onClick={() => setViewAll(false)}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                >
                  ← Show less
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <BlogSidebar
              posts={recentPosts}
              allPosts={blogPosts}
            />
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <Suspense fallback={null}>
          <BlogModal
            post={selectedPost}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
        </Suspense>
      )}
    </div>
  );
};

export default BlogPage;